import { DateTime } from 'luxon';
import { DatabaseUpdateParser, getFirebaseServices, getAuthenticatedUserInfo } from '../DatabaseUpdateParser.js';

/** @import { UpdateParserOverrides } from '../DatabaseUpdateParser.js' */

const SHARING_PROPAGATION_DELAY_MS = 5000;
const THROTTLE_DELAY_MS = 1000;

/**
 * Get a parser that updates the last updated timestamp of a job
 * @param {UpdateParserOverrides?} [OVERRIDES] - Overrides for using this parser in different contexts (e.g. on the server with admin)
 * @returns {DatabaseUpdateParser} A job-last-updated parser
 */
export function getLastUpdatedParser(OVERRIDES = null) {
  /**
   * @typedef {object} TimestampDetails
   * @property {number} lastUpdatedAt - The timestamp of the last update in milliseconds
   * @property {boolean} waitingToUpdateAgain - Whether a future timestamp update has been scheduled for this job
   */
  /** @type {Record<string, TimestampDetails>} */
  const timestampDetailsPerJob = {};

  return DatabaseUpdateParser.fromConfig({
    globalPathFilter: /^photoheight\/jobs\/*./,
    eventTypes: [
      {
        name: 'LAST_UPDATED',
        pathMatch: /^photoheight\/jobs\/(?<jobId>[^\/\s]+)(?:\/.*)?$/,
        parse: async ({ jobId }) => ({ jobId }),
        onEventGroup: async (events) => {
          const { uid: userId } = await getAuthenticatedUserInfo(OVERRIDES);
          // Get a list of all the job IDs that were updated
          const jobIds = [...new Set(events.map((event) => event.jobId))];

          for (const jobId of jobIds) {
            timestampDetailsPerJob[jobId] ??= { lastUpdatedAt: 0, waitingToUpdateAgain: false };
            const timestampDetails = timestampDetailsPerJob[jobId];

            // Determine how long it has been since we last updated this job's timestamp.
            const updateRequestedAt = DateTime.now().toMillis();
            const lastUpdatedAt = timestampDetails.lastUpdatedAt;
            const timeSinceLastUpdate = updateRequestedAt - lastUpdatedAt;

            // If it has been long enough since the last update for this job, update the timestamp now.
            if (timeSinceLastUpdate >= THROTTLE_DELAY_MS) {
              timestampDetails.lastUpdatedAt = DateTime.now().toMillis();
              await updateJobTimestamp(jobId, userId, OVERRIDES);
              continue;
            }

            // See if we have already scheduled a future timestamp update for this job.
            // If we have, we can skip scheduling another and move onto the next job.
            const futureUpdateScheduled = timestampDetails.waitingToUpdateAgain;
            if (futureUpdateScheduled) {
              continue;
            }

            // If we haven't already scheduled a future timestamp update for this job,
            // do so now before moving onto the next job.
            const timeToWait = THROTTLE_DELAY_MS - timeSinceLastUpdate;
            (async () => {
              timestampDetails.waitingToUpdateAgain = true;
              await new Promise((resolve) => setTimeout(resolve, timeToWait));
              timestampDetails.waitingToUpdateAgain = false;
              timestampDetails.lastUpdatedAt = DateTime.now().toMillis();
              await updateJobTimestamp(jobId, userId, OVERRIDES);
            })();
          }
        }
      }
    ]
  });
}

/**
 * Update the job timestamp in both the company jobs_last_updated and the jobs collection
 * @param {string} jobId - Job ID of the job being updated
 * @param {?string} userId - User ID of the user making the update
 * @param {?UpdateParserOverrides} OVERRIDES - Overrides for using this parser in different contexts (e.g. on the server with admin)
 * @param {boolean} [shouldRetryAfterFailure=true] - Whether this is a retry attempt after a failure
 */
async function updateJobTimestamp(jobId, userId, OVERRIDES, shouldRetryAfterFailure = true) {
  const { firestoreDB, realtimeDB, FieldValue } = getFirebaseServices(OVERRIDES);

  // Prepare the fields to update with the current timestamp and user's id.
  const updateFields = {
    last_updated: FieldValue.serverTimestamp(),
    last_updated_by: userId
  };

  // If the update is coming from a mobile client, also update the mobile-specific fields.
  if (globalThis.isMobile)
    Object.assign(updateFields, {
      mobile_last_updated: FieldValue.serverTimestamp(),
      mobile_last_updated_by: userId
    });

  try {
    // Get the job owner for this job
    const jobOwner = (await realtimeDB.ref(`photoheight/jobs/${jobId}/job_owner`).once('value')).val();
    const companyDoc = firestoreDB.doc(`companies/${jobOwner}/jobs_last_updated/${jobId}`);
    const jobDoc = firestoreDB.doc(`jobs/${jobId}`);
    const batch = firestoreDB.batch();
    batch.set(companyDoc, updateFields, { merge: true });
    batch.set(jobDoc, { metadata: updateFields }, { merge: true });
    await batch.commit();
  } catch (err) {
    if (shouldRetryAfterFailure) {
      // In the case of an error, we might be creating the job for the first time.
      // In that case, it will take some time for the sharing object to be
      // populated, so wait a bit and then try again.
      await new Promise((resolve) => setTimeout(resolve, SHARING_PROPAGATION_DELAY_MS));
      return updateJobTimestamp(jobId, userId, OVERRIDES, false);
    } else {
      throw err;
    }
  }
}
